// ──────────────────────────────────────────────
// SessionCompletionService — orchestrates the recording stop → drain → recording-end flow.
//
// See ADR-007 for design rationale.
// ──────────────────────────────────────────────

import APIService from '@services/APIService';
import SentryService from '@services/SentryService';

import type { StorageService } from '../storage';
import { BaseService } from '../upload/BaseService';
import type { UploadService } from '../upload/UploadService';

import type { SessionManifest } from './SessionManifest';
import type {
  CompletionResult,
  SessionCompletionEventMap,
  SessionCompletionState,
  SessionCompletionStatus,
} from './types';

// ── Constants ──

const DEFAULT_DRAIN_TIMEOUT_MS = 30_000;
const RECORDING_END_MAX_RETRIES = 3;
const RECORDING_END_RETRY_BASE_MS = 2000;

function initialState(): SessionCompletionState {
  return {
    status: 'idle',
    totalChunks: 0,
    uploadedChunks: 0,
    failedChunks: 0,
    drainElapsedSeconds: 0,
    drainTimeoutSeconds: DEFAULT_DRAIN_TIMEOUT_MS / 1000,
    canGenerateNote: false,
    offlineChunksPending: 0,
  };
}

export interface SessionCompletionConfig {
  uploadService: UploadService;
  storageService: StorageService;
  sessionManifest: SessionManifest;
  getAccessToken: () => Promise<string>;
  drainTimeoutMs?: number;
}

/**
 * Orchestrates the recording stop → upload drain → recording-end API call flow.
 *
 * After the provider hits "stop", this service:
 * 1. Waits for UploadService to drain (with timeout)
 * 2. Cleans up local storage + manifest (before status transition to prevent stale data on reload)
 * 3. Calls the recording-end API (best-effort)
 * 4. Emits 'completed' when safe to generate notes
 */
export class SessionCompletionService extends BaseService<SessionCompletionState, SessionCompletionEventMap> {
  private readonly uploadService: UploadService;
  private readonly storageService: StorageService;
  private readonly sessionManifest: SessionManifest;
  private readonly getAccessToken: () => Promise<string>;
  private readonly drainTimeoutMs: number;

  private drainTimerInterval: ReturnType<typeof setInterval> | null = null;
  private disposed = false;
  private networkWaitReject: (() => void) | null = null;

  constructor(config: SessionCompletionConfig) {
    super(initialState());

    this.uploadService = config.uploadService;
    this.storageService = config.storageService;
    this.sessionManifest = config.sessionManifest;
    this.getAccessToken = config.getAccessToken;
    this.drainTimeoutMs = config.drainTimeoutMs ?? DEFAULT_DRAIN_TIMEOUT_MS;
  }

  // ── Public API ──

  /**
   * Called when recording stops — starts the drain → recording-end flow.
   * Returns a CompletionResult when the full flow is done.
   */
  async startCompletion(sessionId: string, totalChunks: number, recordingSessionLogs?: string): Promise<CompletionResult> {
    return this.completeSession(sessionId, totalChunks, recordingSessionLogs);
  }

  /**
   * Complete a session: drain uploads, call recording-end, update manifest.
   * Used by both normal stop (startCompletion) and crash recovery.
   */
  async completeSession(sessionId: string, totalChunks: number, recordingSessionLogs?: string): Promise<CompletionResult> {
    if (this.disposed) {
      throw new Error('SessionCompletionService is disposed');
    }

    SentryService.addBreadcrumb({
      category: 'session.completion',
      message: `Starting completion for session ${sessionId} (${totalChunks} chunks)`,
      level: 'info',
    });

    // If offline, defer drain until network returns
    if (!this.uploadService.getSnapshot().isOnline) {
      this.setCompletionStatus('offline_pending');
      this.setState({
        ...this.state,
        totalChunks,
        uploadedChunks: 0,
        failedChunks: 0,
        drainElapsedSeconds: 0,
        drainTimeoutSeconds: this.drainTimeoutMs / 1000,
        canGenerateNote: false,
        offlineChunksPending: totalChunks,
      });

      SentryService.addBreadcrumb({
        category: 'session.completion',
        message: `Offline — deferring drain for session ${sessionId}`,
        level: 'warning',
      });

      await this.waitForNetwork();

      // Re-check disposal after async wait
      if (this.disposed) {
        throw new Error('SessionCompletionService disposed while waiting for network');
      }
    }

    // Now online — proceed with drain
    this.setCompletionStatus('draining');
    this.setState({
      ...this.state,
      totalChunks,
      uploadedChunks: 0,
      failedChunks: 0,
      drainElapsedSeconds: 0,
      drainTimeoutSeconds: this.drainTimeoutMs / 1000,
      canGenerateNote: false,
      offlineChunksPending: 0,
    });

    // Start drain elapsed timer (1s interval)
    const drainStart = Date.now();
    this.drainTimerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - drainStart) / 1000);
      this.setState({ ...this.state, drainElapsedSeconds: elapsed });
    }, 1000);

    try {
      // 1. Wait for UploadService to drain
      const drainResult = await this.uploadService.drain(this.drainTimeoutMs);

      this.clearDrainTimer();

      const drainDurationMs = Date.now() - drainStart;
      const uploadedChunks = drainResult.completedCount;
      const failedChunks = drainResult.failedCount;

      this.setState({
        ...this.state,
        uploadedChunks,
        failedChunks,
        drainElapsedSeconds: Math.floor(drainDurationMs / 1000),
      });

      // 2. Clean up local data before status transition.
      // This prevents stale manifests causing duplicate documents on page reload.
      // Recording-end API only needs sessionId, not local data.
      await this.storageService.deleteSession(sessionId);
      await this.sessionManifest.delete(sessionId);

      // 3. Determine completion status
      if (failedChunks > 0) {
        this.setCompletionStatus('completed_with_losses');
      } else {
        this.setCompletionStatus('completed');
      }

      // 4. Call recording-end API (best-effort — manifest already cleaned up)
      const recordingEndConfirmed = await this.callRecordingEnd(sessionId, totalChunks, recordingSessionLogs ?? '');

      // 5. Set canGenerateNote
      this.setState({ ...this.state, canGenerateNote: true });

      const result: CompletionResult = {
        sessionId,
        totalChunks,
        uploadedChunks,
        failedChunks,
        drainDurationMs,
        timedOut: drainResult.timedOut,
        recordingEndConfirmed,
      };

      this.emit('completed', { sessionId, result });

      SentryService.addBreadcrumb({
        category: 'session.completion',
        message: `Session ${sessionId} completed (uploaded=${uploadedChunks}, failed=${failedChunks}, timedOut=${drainResult.timedOut})`,
        level: 'info',
      });

      return result;
    } catch (error) {
      this.clearDrainTimer();

      const message = error instanceof Error ? error.message : String(error);
      this.emit('error', { message, cause: error });

      SentryService.captureException(
        error instanceof Error ? error : new Error(`Session completion failed: ${message}`),
      );

      throw error;
    }
  }

  /**
   * Reset state to initial idle. Called when a new recording starts
   * to ensure `canGenerateNote` is false for the new session.
   */
  reset(): void {
    this.clearDrainTimer();
    this.setState(initialState());
  }

  override dispose(): void {
    this.disposed = true;
    this.networkWaitReject?.();
    this.networkWaitReject = null;
    this.clearDrainTimer();
    this.setState(initialState());
    super.dispose();
  }

  // ── Private ──

  /**
   * Call the recording-end API with retries.
   * Returns true if the backend confirmed, false if all retries exhausted.
   */
  private async callRecordingEnd(sessionId: string, totalChunks: number, recordingSessionLogs: string): Promise<boolean> {
    for (let attempt = 0; attempt < RECORDING_END_MAX_RETRIES; attempt++) {
      try {
        const accessToken = await this.getAccessToken();

        const response = await APIService.makeAPIPostRequest({
          requestString: '/transcriptionSession/endTranscriptionSession',
          accessToken,
          body: {
            transcription_session_id: sessionId,
            recording_session_logs: recordingSessionLogs,
            expected_chunk_count: totalChunks,
          },
          retries: 0, // We manage retries ourselves
        });

        if (response.ok) {
          SentryService.addBreadcrumb({
            category: 'session.completion',
            message: `Recording-end confirmed for session ${sessionId}`,
            level: 'info',
          });
          return true;
        }

        SentryService.addBreadcrumb({
          category: 'session.completion',
          message: `Recording-end failed (attempt ${attempt + 1}/${RECORDING_END_MAX_RETRIES}): ${response.error?.message ?? 'unknown'}`,
          level: 'warning',
        });
      } catch (error) {
        SentryService.addBreadcrumb({
          category: 'session.completion',
          message: `Recording-end error (attempt ${attempt + 1}/${RECORDING_END_MAX_RETRIES}): ${error instanceof Error ? error.message : String(error)}`,
          level: 'warning',
        });
      }

      // Backoff before retry (skip for last attempt)
      if (attempt < RECORDING_END_MAX_RETRIES - 1) {
        const delay = RECORDING_END_RETRY_BASE_MS * Math.pow(2, attempt);
        await new Promise(r => setTimeout(r, delay));
      }
    }

    SentryService.captureException(
      new Error(`Recording-end exhausted all retries for session ${sessionId}`),
    );

    return false;
  }

  private setCompletionStatus(status: SessionCompletionStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.setState({ ...this.state, status });
    this.emit('statusChanged', { status, prev });
  }

  /**
   * Returns a promise that resolves when the network comes back online.
   * Rejects if the service is disposed while waiting.
   */
  private waitForNetwork(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const handler = ({ isOnline }: { isOnline: boolean }) => {
        if (isOnline) {
          this.networkWaitReject = null;
          this.uploadService.off('networkChanged', handler);
          resolve();
        }
      };

      this.networkWaitReject = () => {
        this.uploadService.off('networkChanged', handler);
        reject(new Error('Disposed while waiting for network'));
      };

      this.uploadService.on('networkChanged', handler);
    });
  }

  private clearDrainTimer(): void {
    if (this.drainTimerInterval) {
      clearInterval(this.drainTimerInterval);
      this.drainTimerInterval = null;
    }
  }
}
